import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';
import { Save, Clock, FileText, DollarSign, Gift } from 'lucide-react';

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    trial_duration_days: 3,
    starter_record_limit: 20,
    growth_record_limit: 100,
    pro_monthly_price: 9.90,
    referral_reward_days: 14
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Settings" breadcrumbs={[{ label: 'Settings' }]}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B5AFF]"></div>
        </div>
      </AdminLayout>
    );
  }

  const saveButton = (
    <Button onClick={handleSave} disabled={saving} className="bg-[#1B5AFF] hover:bg-[#144BD6] text-[#FFFFFF]">
      <Save className="w-4 h-4 mr-2" />
      {saving ? 'Saving...' : 'Save Changes'}
    </Button>
  );

  return (
    <AdminLayout 
      title="Settings" 
      breadcrumbs={[{ label: 'Settings' }]}
      actions={saveButton}
    >
      <div className="max-w-3xl space-y-6">
        {/* Trial Settings */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1B5AFF]" />
            Trial Settings
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-[#5B6472] text-sm">Trial Duration (Days)</Label>
              <Input
                type="number"
                value={settings.trial_duration_days}
                onChange={(e) => handleChange('trial_duration_days', parseInt(e.target.value) || 0)}
                className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
              />
              <p className="text-[#5B6472] text-xs mt-1">Number of days for new employer free trial</p>
            </div>
          </div>
        </Card>

        {/* Record Limits */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#1B5AFF]" />
            Record Limits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#5B6472] text-sm">Starter Plan Record Limit</Label>
              <Input
                type="number"
                value={settings.starter_record_limit}
                onChange={(e) => handleChange('starter_record_limit', parseInt(e.target.value) || 0)}
                className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
              />
            </div>
            <div>
              <Label className="text-[#5B6472] text-sm">Growth Plan Record Limit</Label>
              <Input
                type="number"
                value={settings.growth_record_limit}
                onChange={(e) => handleChange('growth_record_limit', parseInt(e.target.value) || 0)}
                className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
              />
            </div>
          </div>
        </Card>

        {/* Pricing */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#1B5AFF]" />
            Pricing
          </h3>
          <div>
            <Label className="text-[#5B6472] text-sm">Employee Pro Monthly Price (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={settings.pro_monthly_price}
              onChange={(e) => handleChange('pro_monthly_price', parseFloat(e.target.value) || 0)}
              className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
            />
            <p className="text-[#5B6472] text-xs mt-1">Monthly price for Employee Pro subscription</p>
          </div>
        </Card>

        {/* Referral Settings */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-bold text-[#0A0F1E] mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#1B5AFF]" />
            Referral Program
          </h3>
          <div>
            <Label className="text-[#5B6472] text-sm">Referral Reward Days</Label>
            <Input
              type="number"
              value={settings.referral_reward_days}
              onChange={(e) => handleChange('referral_reward_days', parseInt(e.target.value) || 0)}
              className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
            />
            <p className="text-[#5B6472] text-xs mt-1">Extra trial days awarded for successful referrals</p>
          </div>
        </Card>

        {/* Info */}
        <Card className="bg-[rgba(240,180,41,0.1)] border border-[rgba(240,180,41,0.3)] rounded-xl p-4">
          <p className="text-[#F0B429] text-sm">
            <strong>Note:</strong> Changes to these settings take effect immediately for new users and transactions. 
            Existing subscriptions and trials will not be affected.
          </p>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
